/**
 * Client-side validation for the commission form.
 *
 * The backend validates all of this too (pydantic `Field` constraints
 * on `CommissionSiteRequest`) and is the authority. What is here is
 * for the errors worth catching before a round trip — plus one rule
 * the backend cannot express.
 */

/** Mirrors `CommissionSiteRequest.site_id`'s pattern exactly. The site
 *  id is not just a key: it becomes the certificate CN and the ACL
 *  subject, so it lands in a TLS certificate and a zenoh key
 *  expression. Neither tolerates the characters this rejects. */
export const SITE_ID_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

export function validateSiteId(value: string): string | null {
  if (value.length === 0) return 'Required.';
  if (value.length > 64) return 'Must be 64 characters or fewer.';
  if (!SITE_ID_PATTERN.test(value)) {
    return 'Lowercase letters, digits, hyphen and underscore only, starting with a letter or digit. This becomes the certificate CN and the bus ACL subject.';
  }
  return null;
}

/**
 * Looks like an env var name (`WAREHOUSE3_BROKER_TOKEN`) or a secret
 * path (`/run/secrets/...`, `vault:kv/...`)?
 *
 * `broker_token_ref` must hold a REFERENCE, never the credential —
 * the same rule `DeploymentBinding` documents on the column itself.
 * The backend cannot enforce this: a raw token and a reference are
 * both just strings, and any pattern strict enough to reject every
 * token would reject legitimate reference formats too. So the check
 * lives here, where it can warn without blocking.
 */
export function looksLikeReference(value: string): boolean {
  if (value.length === 0) return true;
  if (/^[A-Z][A-Z0-9_]*$/.test(value)) return true; // env var name
  if (value.startsWith('/')) return true; // file path
  if (/^[a-z][a-z0-9+.-]*:/.test(value)) return true; // vault:, aws:, file: ...
  return false;
}

/**
 * A JWT is unambiguously a credential — three base64url segments
 * separated by dots is not a shape any reference format takes. This
 * is the one case worth BLOCKING rather than warning about, because
 * there is no false positive to worry about.
 */
export function looksLikeJwt(value: string): boolean {
  return /^[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}$/.test(value.trim());
}

/**
 * Task-retention day counts. Mirrors the backend's pydantic ranges
 * (live 7–365, history 30–730); the cross-field rule (history >= live)
 * lives in the form, since it needs both values.
 */
export function validateRetentionDays(value: string, min: number, max: number): string | null {
  const trimmed = value.trim();
  if (trimmed === '' || !/^\d+$/.test(trimmed)) return 'Enter a whole number of days.';
  const n = Number(trimmed);
  if (n < min || n > max) return `Must be between ${min} and ${max} days.`;
  return null;
}
